<script>
import baseMixin from '@/mixins/baseMixin'
import timesheetMixin from '@/mixins/timesheetMixin'
import CandidateImage from '@/components/candidate/candidate-image.vue'
import { TimesheetOperationType } from '@/shared/constants/timesheet/TimesheetOperationType'
import {
  BookingStatus,
  hasCandidateModifiedBookingOrStatedDidNotHappen,
} from '@/shared/constants/booking/BookingStatus'
import { TimesheetPrimaryGroupingType } from '@/shared/constants/timesheet/TimesheetPrimaryGroupingType'
import TimesheetRecordsTable from './timesheet-records-table.vue'

export default {
  name: 'TimesheetLeafGroupCard',
  components: {
    CandidateImage,
    TimesheetRecordsTable,
  },
  mixins: [baseMixin, timesheetMixin],
  props: {
    leafGroupTimeSheets: {
      type: Array,
      required: true,
    },
    primaryGroupType: {
      type: String,
      default: '',
    },
  },
  data: () => {
    return {
      selectedTimesheets: [],
      TimesheetOperationType,
      candidateId: -1,
      candidateFirstName: null,
      candidateLastName: null,
      candidateFullName: '',
      clientName: '',
    }
  },
  computed: {
    compUnmodifiedBookings() {
      return this.leafGroupTimeSheets.filter(
        (timesheet) =>
          !hasCandidateModifiedBookingOrStatedDidNotHappen(
            timesheet.bookingStatus
          )
      )
    },
    hasUnmodifiedBookings() {
      return this.compUnmodifiedBookings.length > 0
    },
    compModifiedBookings() {
      return this.leafGroupTimeSheets.filter(
        (timesheet) =>
          timesheet.bookingStatus ===
          BookingStatus.CANDIDATE_SUBMITTED_TIMESHEET_MODIFIED
      )
    },
    hasModifiedBookings() {
      return this.compModifiedBookings.length > 0
    },
    compDidNotHappenBookings() {
      return this.leafGroupTimeSheets.filter(
        (timesheet) =>
          timesheet.bookingStatus ===
          BookingStatus.CANDIDATE_SUBMITTED_TIMESHEET_DID_NOT_HAPPEN
      )
    },
    hasDidNotHappenBookings() {
      return this.compDidNotHappenBookings.length > 0
    },
    isPrimaryGroupingByClient() {
      return this.primaryGroupType === TimesheetPrimaryGroupingType.client
    },
  },
  created() {
    this.candidateId = this.leafGroupTimeSheets[0]?.candidateId ?? -1
    this.candidateFirstName =
      this.leafGroupTimeSheets[0]?.candidateFirstName ?? ''
    this.candidateLastName =
      this.leafGroupTimeSheets[0]?.candidateLastName ?? ''
    this.candidateFullName =
      this.leafGroupTimeSheets[0]?.candidateFullName ?? ''
    this.clientName = this.leafGroupTimeSheets[0]?.clientName ?? ''
  },
  methods: {
    changeTime(payload) {
      this.$emit('changeTime', payload)
    },
    cancelBookingSubmitted(payload) {
      this.$emit('cancelBookingSubmitted', payload)
    },
    emitSelectedTable(payload) {
      this.selectedTimesheets = payload?.timesheets ?? this.selectedTimesheets

      this.$emit('timesheetsUpdated', {
        key: payload?.key,
        timesheets: this.selectedTimesheets,
      })
    },
  },
}
</script>

<template>
  <v-card class="custom-elevation rounded-xl">
    <v-card-title>
      <div
        v-if="isPrimaryGroupingByClient"
        data-testid="timesheet-leaf-group-card-candidate-title"
        class="d-flex align-center"
      >
        <v-lazy>
          <CandidateImage
            :id="candidateId"
            :first-name="candidateFirstName"
            :last-name="candidateLastName"
            size="md"
          />
        </v-lazy>
        <p class="ml-3 my-0 subtitle-2">
          {{ candidateFullName }}
        </p>
      </div>
      <div
        v-else
        data-testid="timesheet-leaf-group-card-client-title"
        class="d-flex align-center"
      >
        <p class="my-0 subtitle-2">
          {{ clientName }}
        </p>
      </div>
    </v-card-title>

    <v-card-text v-if="hasUnmodifiedBookings">
      <p class="text-body-2 font-weight-medium mb-0">{{
        $t('timesheets.table.sections.unmodifiedTitle')
      }}</p>
    </v-card-text>
    <TimesheetRecordsTable
      v-if="hasUnmodifiedBookings"
      data-testid="timesheet-leaf-group-card-table-unmodified"
      :timesheets="compUnmodifiedBookings"
      @timesheetsUpdated="emitSelectedTable"
      @changeTime="changeTime"
      @cancelBookingSubmitted="cancelBookingSubmitted"
    />

    <v-card-text v-if="hasModifiedBookings">
      <p class="text-body-2 font-weight-medium mb-0">{{
        $t('timesheets.table.sections.modifiedTitle')
      }}</p>
    </v-card-text>
    <TimesheetRecordsTable
      v-if="hasModifiedBookings"
      data-testid="timesheet-leaf-group-card-table-modified"
      :timesheets="compModifiedBookings"
      @timesheetsUpdated="emitSelectedTable"
      @changeTime="changeTime"
      @cancelBookingSubmitted="cancelBookingSubmitted"
    />

    <v-card-text v-if="hasDidNotHappenBookings">
      <p class="text-body-2 font-weight-medium mb-0">{{
        $t('timesheets.table.sections.didNotHappenTitle')
      }}</p>
    </v-card-text>
    <TimesheetRecordsTable
      v-if="hasDidNotHappenBookings"
      data-testid="timesheet-leaf-group-card-table-didNotHappen"
      :timesheets="compDidNotHappenBookings"
      @timesheetsUpdated="emitSelectedTable"
      @changeTime="changeTime"
      @cancelBookingSubmitted="cancelBookingSubmitted"
    />
  </v-card>
</template>
